import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentArrowDown,
  HiOutlineCalendar,
  HiOutlineClipboardDocumentCheck,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from 'react-icons/hi2';
import pmStandardService from '../services/pmStandardService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './Reports.css';

import plantService from '../services/plantService';
import lineService from '../services/lineService';
import machineService from '../services/machineService';
import MultiSelectDropdown from '../components/MultiSelectDropdown';

import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
const PmScheduleComplianceReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  // Filter states
  const [plants, setPlants] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const pData = await plantService.getAllPlants();
      setPlants(pData || []);
      
      const lData = await lineService.getAllLines();
      setAllLines(lData || []);
      
      const mData = await machineService.getAllMachines();
      setAllMachines(mData || []);

      // Auto-select plant if only 1 exists
      if (pData?.length === 1) {
        const pId = pData[0].plantId;
        setSelectedPlants([pId]);
      }

      // Auto-select all lines ONLY for single-plant users
      if (pData?.length === 1 && selectedLines.length === 0) {
        const pId = pData[0].plantId;
        const plantLines = (lData || []).filter(l => l.plantId === pId).map(l => l.lineId);
        if (plantLines.length > 0) {
          setSelectedLines(plantLines);
        }
      }
    } catch (err) {
      console.error('Error loading filter data:', err);
    }
  };

  const fetchData = async () => {
    showReportGeneratingToast();
    try {
      setLoading(true);
      const res = await pmStandardService.getPmComplianceReport(
        dateRange.startDate, 
        dateRange.endDate,
        selectedPlants,
        selectedLines,
        selectedMachines
      );
      setData(res || []);
      reportGenerationDone();
    } catch (err) {
      console.error('Error fetching compliance report:', err);
      reportGenerationFailed('Could not load compliance report.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  // Cascading logic
  const lineOptions = selectedPlants.length > 0
    ? allLines.filter(l => selectedPlants.includes(l.plantId))
    : allLines;

  const machineOptions = selectedLines.length > 0
    ? allMachines.filter(m => selectedLines.includes(m.lineId))
    : selectedPlants.length > 0
      ? allMachines.filter(m => lineOptions.some(l => l.lineId === m.lineId))
      : allMachines;

  const handlePlantChange = (vals) => {
    setSelectedPlants(vals);
    // Reset downstream filters if they are no longer valid
    const validLines = allLines.filter(l => vals.includes(l.plantId)).map(l => l.lineId);
    setSelectedLines(prev => prev.filter(id => validLines.includes(id)));
  };

  const handleLineChange = (vals) => {
    setSelectedLines(vals);
    // Reset machines
    const validMachines = allMachines.filter(m => vals.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const exportToExcel = () => {
    if (data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        Plant: item.plantName,
        Line: item.lineName,
        Machine: item.machineName,
        'Total Scheduled': item.totalScheduled,
        'Completed On-Time': item.completedOnTime,
        Overdue: item.overdue,
        'Open / Due Today': item.open || 0,
        'Compliance Rate (%)': item.complianceRate,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Compliance Report');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, `PM_Compliance_Report_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
  };

  const stats = [
    {
      label: 'Overall Compliance',
      value:
        data && data.length > 0
          ? (() => {
              const totalScheduled = data.reduce((acc, curr) => acc + curr.totalScheduled, 0);
              const totalOnTime = data.reduce((acc, curr) => acc + curr.completedOnTime, 0);
              return totalScheduled > 0 ? ((totalOnTime * 100) / totalScheduled).toFixed(1) + '%' : '0%';
            })()
          : '0%',
      sub: 'Total On-Time / Total Planned',
      color: SEMANTIC_SUCCESS_MID,
      borderColor: SEMANTIC_SUCCESS_MID,
    },
    {
      label: 'Total PMs',
      value: (data || []).reduce((acc, curr) => acc + curr.totalScheduled, 0),
      sub: 'Scheduled in period',
      color: '#3b82f6',
      borderColor: '#3b82f6',
    },
    {
      label: 'On-Time',
      value: (data || []).reduce((acc, curr) => acc + curr.completedOnTime, 0),
      sub: 'Completed within window',
      color: 'var(--accent-primary)',
      borderColor: 'var(--accent-primary)',
    },
    {
      label: 'Total Overdue',
      value: (data || []).reduce((acc, curr) => acc + curr.overdue, 0),
      sub: 'Still pending / missed',
      color: '#ef4444',
      borderColor: '#ef4444',
    },
    {
      label: 'Open / Due Today',
      value: (data || []).reduce((acc, curr) => acc + (curr.open || 0), 0),
      sub: 'Scheduled for today',
      color: '#3b82f6',
      borderColor: '#3b82f6',
    },
  ];

  return (
    <div className="pagecontainer report-page">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="oee-report-header-left">
          <button
            className="oee-report-back-btn"
            onClick={() => navigate('/home/reports')}
            title="Back to Reports"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineClipboardDocumentCheck /> PM Schedule Compliance
            </h1>
            <p className="oee-report-subtitle">
              Monitor planned maintenance execution performance and compliance rates
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="oee-filter-toggle-btn"
            onClick={() => setShowFilters((p) => !p)}
          >
            <HiOutlineCalendar size={16} />
            Filters
            {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button
            className="btn-primary btn"
            onClick={exportToExcel}
            disabled={loading || !data || data.length === 0}
            title={!data || data.length === 0 ? 'Generate a report first' : 'Export to Excel'}
          >
            <HiOutlineDocumentArrowDown /> Export
          </button>
        </div>
      </div>

      {/* ── Filter Panel ── */}
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                className="filter-input"
                value={dateRange.startDate}
                onChange={handleDateChange}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                className="filter-input"
                value={dateRange.endDate}
                onChange={handleDateChange}
              />
            </div>
            <div className="filter-group">
              <label>Plant</label>
              <MultiSelectDropdown
                options={plants.map(p => ({ id: p.plantId, name: p.plantName }))}
                selectedValues={selectedPlants}
                onChange={handlePlantChange}
                placeholder="All Plants"
                displayKey="name"
                valueKey="id"
              />
            </div>
            <div className="filter-group">
              <label>LINE</label>
              <MultiSelectDropdown
                options={lineOptions.map(l => ({ id: l.lineId, name: l.lineName }))}
                selectedValues={selectedLines}
                onChange={handleLineChange}
                placeholder="All Lines"
                displayKey="name"
                valueKey="id"
              />
            </div>
            <div className="filter-group">
              <label>Machine</label>
              <MultiSelectDropdown
                options={machineOptions.map(m => ({ id: m.machineId, name: m.machineName }))}
                selectedValues={selectedMachines}
                onChange={setSelectedMachines}
                placeholder="All Machines"
                displayKey="name"
                valueKey="id"
              />
            </div>
            <div className="filter-group" style={{ justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
              <label>&nbsp;</label>
              <button type="button" className="btn-primary report-generate-btn" onClick={fetchData} disabled={loading}>
                <HiOutlineCalendar /> Generate Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── KPI Cards ── */}
      {data && data.length > 0 && (
        <div className='statsMainBox'>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                background: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0; border-color: var(--border-primary)',
                borderLeft: `4px solid ${s.borderColor}`,
                padding: '18px 20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              }}
            >
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</p>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: 700, color: s.color }}>{s.value}</h2>
              <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Table ── */}
      <div className="reports-table">
        <div className="table-header text-left" style={{ textAlign: 'left', justifyContent: 'flex-end' }}>
          <h3 style={{ margin: 0 }}>Compliance Details</h3>
        </div>
        {loading ? (
          <div className="loading-container">Calculating compliance rates…</div>
        ) : !data ? (
          <div className="report-placeholder" style={{ padding: '5rem 2rem', textAlign: 'center' }}>
            <HiOutlineClipboardDocumentCheck style={{ fontSize: '4rem', color: '#64748b', opacity: 0.3, marginBottom: '1.5rem' }} />
            <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e293b', margin: '0 0 10px 0' }}>
              PM Schedule Compliance Report
            </h3>
            <p style={{ color: '#64748b', margin: 0 }}>
              Select date range and click "Generate Report" to view analytics
            </p>
          </div>
        ) : (
          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Plant</th>
                  <th>Line</th>
                  <th>Machine</th>
                  <th>Total</th>
                  <th>On-Time</th>
                  <th>Overdue</th>
                  <th>Open / Due</th>
                  <th>Compliance %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.plantName}</td>
                    <td>{row.lineName}</td>
                    <td><strong>{row.machineName}</strong></td>
                    <td>{row.totalScheduled}</td>
                    <td className="text-success"><strong>{row.completedOnTime}</strong></td>
                    <td className={row.overdue > 0 ? 'text-danger' : ''}><strong>{row.overdue}</strong></td>
                    <td style={{ color: '#3b82f6' }}><strong>{row.open || 0}</strong></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${row.complianceRate}%`,
                              height: '100%',
                              background:
                                row.complianceRate > 90
                                  ? SEMANTIC_SUCCESS_MID
                                  : row.complianceRate > 70
                                    ? '#f59e0b'
                                    : '#ef4444',
                            }}
                          />
                        </div>
                        <span style={{ fontWeight: 700, minWidth: '45px' }}>{row.complianceRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && !loading && (
                  <tr>
                    <td colSpan="7" className="no-data">No data found for the selected period</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PmScheduleComplianceReport;
